'use client';

import { useEffect } from 'react';
import { useRouter } from 'next/navigation';
import { useSession, signOut } from 'next-auth/react';
import { useStreams } from '@/hooks/queries/useStreams';
import { useUserProfile } from '@/hooks/queries/useUserProfile';
import {
  HelpCircle,
  Sparkles,
  Mic2,
  Globe,
  Calendar,
  Film,
  Captions,
  ArrowRight,
  Zap,
  Languages,
  Share2,
  ChevronRight,
} from 'lucide-react';
import { WelcomeVideoModal } from '@/components/WelcomeVideoModal';
import { SUPPORT_EMAIL } from '@/lib/constants/contact';

export default function HomePage() {
  const router = useRouter();
  const { data: session, status } = useSession();
  const { data: streams = [], isLoading: streamsLoading } = useStreams(!!session);
  const { data: userProfile } = useUserProfile(session?.user?.email);

  // Redirect to stream route when authenticated and streams are loaded
  useEffect(() => {
    if (status === 'authenticated' && !streamsLoading) {
      if (streams.length > 0) {
        router.replace(`/stream/${streams[0].stream_id}`);
      }
    }
  }, [status, streamsLoading, streams, router]);

  // Loading state
  if (status === 'loading' || (status === 'authenticated' && streamsLoading)) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-zinc-950">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-violet-400"></div>
      </div>
    );
  }

  // Authenticated but no streams
  if (status === 'authenticated' && streams.length === 0) {
    return (
      <div className="min-h-screen flex flex-col bg-zinc-950">
        <div className="flex-1 flex items-center justify-center px-4">
          <div className="text-center max-w-2xl">
            <h1 className="text-3xl sm:text-4xl mb-4">
              <span className="text-white font-bold">Welcome to </span>
              <span className="font-medium tracking-[0.3em] uppercase">
                <span className="text-gray-100">K R E A Y T</span>
                <span className="text-violet-400 font-semibold ml-2">A I</span>
              </span>
            </h1>
            <p className="text-lg sm:text-xl text-zinc-400 mb-8">
              Create your first stream to start generating video content.
            </p>
            <div className="flex flex-col items-center gap-4">
              <div className="flex gap-4 justify-center">
                <button
                  onClick={() => router.push('/streams')}
                  className="bg-violet-600 text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-violet-500 transition-colors"
                >
                  Create Your First Stream
                </button>
                <button
                  onClick={() => signOut({ callbackUrl: '/' })}
                  className="bg-zinc-800 text-white px-8 py-3 rounded-lg text-lg font-medium hover:bg-zinc-700 transition-colors border border-white/[0.08]"
                >
                  Sign Out
                </button>
              </div>
              <a
                href={`mailto:${SUPPORT_EMAIL}`}
                className="flex items-center gap-2 text-zinc-500 hover:text-violet-400 transition-colors"
              >
                <HelpCircle className="w-4 h-4" />
                <span>Questions? {SUPPORT_EMAIL}</span>
              </a>
            </div>
          </div>
        </div>
        <WelcomeVideoModal userCreatedAt={userProfile?.created_at} />
      </div>
    );
  }

  // Not signed in — landing page
  return (
    <div className="min-h-screen flex flex-col bg-zinc-950 text-white overflow-x-hidden">

      {/* ── Hero ── */}
      <section className="relative flex flex-col items-center justify-center text-center px-4 pt-24 pb-20 sm:pt-32 sm:pb-28">
        {/* Gradient glow */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[500px] bg-violet-600/15 rounded-full blur-[120px]" />
        </div>

        <div className="relative z-10 max-w-3xl">
          <h1 className="text-4xl sm:text-5xl md:text-6xl font-medium tracking-[0.3em] uppercase mb-6">
            <span className="text-gray-100">K R E A Y T</span>
            <span className="text-violet-400 font-semibold ml-3">A I</span>
          </h1>
          <p className="text-xl sm:text-2xl text-zinc-300 mb-3 font-light">
            AI-Powered Video Creation Platform
          </p>
          <p className="text-base sm:text-lg text-zinc-500 mb-10 max-w-xl mx-auto leading-relaxed">
            Generate professional short-form videos from text in minutes.
            100+ AI voices, multi-language support, auto-publishing to every major platform.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <button
              onClick={() => router.push('/auth/signin')}
              className="group bg-violet-600 text-white px-8 py-3.5 rounded-lg text-lg font-medium hover:bg-violet-500 transition-all flex items-center gap-2"
            >
              Get Started Free
              <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
            </button>
            <a
              href="#features"
              className="text-zinc-400 hover:text-white transition-colors flex items-center gap-1.5 text-sm"
            >
              See how it works
              <ChevronRight className="w-4 h-4" />
            </a>
          </div>
        </div>
      </section>

      {/* ── Platform Logos ── */}
      <section className="py-10 border-y border-white/[0.04]">
        <div className="max-w-4xl mx-auto px-4">
          <p className="text-xs text-zinc-600 uppercase tracking-widest text-center mb-6">Publish everywhere</p>
          <div className="flex items-center justify-center gap-8 sm:gap-14 text-zinc-500">
            <div className="flex flex-col items-center gap-1.5">
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor"><path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/></svg>
              <span className="text-[10px] tracking-wide">YouTube</span>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor"><path d="M12.525.02c1.31-.02 2.61-.01 3.91-.02.08 1.53.63 3.09 1.75 4.17 1.12 1.11 2.7 1.62 4.24 1.79v4.03c-1.44-.05-2.89-.35-4.2-.97-.57-.26-1.1-.59-1.62-.93-.01 2.92.01 5.84-.02 8.75-.08 1.4-.54 2.79-1.35 3.94-1.31 1.92-3.58 3.17-5.91 3.21-1.43.08-2.86-.31-4.08-1.03-2.02-1.19-3.44-3.37-3.65-5.71-.02-.5-.03-1-.01-1.49.18-1.9 1.12-3.72 2.58-4.96 1.66-1.44 3.98-2.13 6.15-1.72.02 1.48-.04 2.96-.04 4.44-.99-.32-2.15-.23-3.02.37-.63.41-1.11 1.04-1.36 1.75-.21.51-.15 1.07-.14 1.61.24 1.64 1.82 3.02 3.5 2.87 1.12-.01 2.19-.66 2.77-1.61.19-.33.4-.67.41-1.06.1-1.79.06-3.57.07-5.36.01-4.03-.01-8.05.02-12.07z"/></svg>
              <span className="text-[10px] tracking-wide">TikTok</span>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C8.74 0 8.333.015 7.053.072 5.775.132 4.905.333 4.14.63c-.789.306-1.459.717-2.126 1.384S.935 3.35.63 4.14C.333 4.905.131 5.775.072 7.053.012 8.333 0 8.74 0 12s.015 3.667.072 4.947c.06 1.277.261 2.148.558 2.913.306.788.717 1.459 1.384 2.126.667.666 1.336 1.079 2.126 1.384.766.296 1.636.499 2.913.558C8.333 23.988 8.74 24 12 24s3.667-.015 4.947-.072c1.277-.06 2.148-.262 2.913-.558.788-.306 1.459-.718 2.126-1.384.666-.667 1.079-1.335 1.384-2.126.296-.765.499-1.636.558-2.913.06-1.28.072-1.687.072-4.947s-.015-3.667-.072-4.947c-.06-1.277-.262-2.149-.558-2.913-.306-.789-.718-1.459-1.384-2.126C21.319 1.347 20.651.935 19.86.63c-.765-.297-1.636-.499-2.913-.558C15.667.012 15.26 0 12 0zm0 2.16c3.203 0 3.585.016 4.85.071 1.17.055 1.805.249 2.227.415.562.217.96.477 1.382.896.419.42.679.819.896 1.381.164.422.36 1.057.413 2.227.057 1.266.07 1.646.07 4.85s-.015 3.585-.074 4.85c-.061 1.17-.256 1.805-.421 2.227-.224.562-.479.96-.899 1.382-.419.419-.824.679-1.38.896-.42.164-1.065.36-2.235.413-1.274.057-1.649.07-4.859.07-3.211 0-3.586-.015-4.859-.074-1.171-.061-1.816-.256-2.236-.421-.569-.224-.96-.479-1.379-.899-.421-.419-.69-.824-.9-1.38-.165-.42-.359-1.065-.42-2.235-.045-1.26-.061-1.649-.061-4.844 0-3.196.016-3.586.061-4.861.061-1.17.255-1.814.42-2.234.21-.57.479-.96.9-1.381.419-.419.81-.689 1.379-.898.42-.166 1.051-.361 2.221-.421 1.275-.045 1.65-.06 4.859-.06l.045.03zm0 3.678a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 1 0 0-12.324zM12 16c-2.21 0-4-1.79-4-4s1.79-4 4-4 4 1.79 4 4-1.79 4-4 4zm7.846-10.405a1.441 1.441 0 1 1-2.882 0 1.441 1.441 0 0 1 2.882 0z"/></svg>
              <span className="text-[10px] tracking-wide">Instagram</span>
            </div>
            <div className="flex flex-col items-center gap-1.5">
              <svg className="w-7 h-7" viewBox="0 0 24 24" fill="currentColor"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
              <span className="text-[10px] tracking-wide">Facebook</span>
            </div>
          </div>
        </div>
      </section>

      {/* ── Features Grid ── */}
      <section id="features" className="py-20 sm:py-24 px-4">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-semibold text-center mb-3">
            Everything you need to create at scale
          </h2>
          <p className="text-zinc-500 text-center mb-14 max-w-xl mx-auto">
            From script to published video — fully automated, no editing skills required.
          </p>

          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {[
              {
                icon: Sparkles,
                title: 'AI Content Generation',
                desc: 'Generate scripts, quotes, and stories from any topic using advanced AI models.',
              },
              {
                icon: Mic2,
                title: '100+ AI Voices',
                desc: 'Natural-sounding voiceovers from AWS Polly, Google Cloud, and Gemini TTS.',
              },
              {
                icon: Languages,
                title: 'Multi-Language',
                desc: 'Create content in 30+ languages with native voice actors and localized captions.',
              },
              {
                icon: Film,
                title: 'Auto Video Assembly',
                desc: 'AI selects visuals, applies transitions, and renders production-ready videos.',
              },
              {
                icon: Captions,
                title: 'Synced Captions',
                desc: 'Word-level caption sync with customizable fonts, colors, and emphasis styles.',
              },
              {
                icon: Calendar,
                title: 'Auto-Scheduling',
                desc: 'Set a publishing schedule and let Kreayt post videos automatically.',
              },
            ].map((f) => (
              <div
                key={f.title}
                className="group rounded-xl border border-white/[0.06] bg-white/[0.02] p-6 hover:border-violet-500/20 hover:bg-violet-500/[0.03] transition-all"
              >
                <f.icon className="w-8 h-8 text-violet-400 mb-4 group-hover:scale-110 transition-transform" />
                <h3 className="text-base font-semibold mb-2">{f.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{f.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── How It Works ── */}
      <section className="py-20 sm:py-24 px-4 border-y border-white/[0.04] bg-white/[0.01]">
        <div className="max-w-4xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-semibold text-center mb-14">
            Three steps to your first video
          </h2>

          <div className="grid sm:grid-cols-3 gap-8 sm:gap-6">
            {[
              {
                step: '01',
                icon: Zap,
                title: 'Create a Stream',
                desc: 'Define your content theme, language, voice actor, and visual style.',
              },
              {
                step: '02',
                icon: Sparkles,
                title: 'Generate Content',
                desc: 'AI creates scripts, voiceovers, visuals, and captions — all in one click.',
              },
              {
                step: '03',
                icon: Share2,
                title: 'Publish Everywhere',
                desc: 'Auto-publish to YouTube, TikTok, Instagram, and Facebook on your schedule.',
              },
            ].map((s, i) => (
              <div key={s.step} className="text-center">
                <div className="w-14 h-14 rounded-full bg-violet-600/10 border border-violet-500/20 flex items-center justify-center mx-auto mb-5">
                  <s.icon className="w-6 h-6 text-violet-400" />
                </div>
                <p className="text-xs text-violet-400 font-mono tracking-widest mb-2">{s.step}</p>
                <h3 className="text-lg font-semibold mb-2">{s.title}</h3>
                <p className="text-sm text-zinc-500 leading-relaxed">{s.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── Stats / Social Proof ── */}
      <section className="py-16 px-4">
        <div className="max-w-4xl mx-auto grid grid-cols-2 sm:grid-cols-4 gap-6 text-center">
          {[
            { value: '100+', label: 'AI Voices' },
            { value: '30+', label: 'Languages' },
            { value: '4', label: 'Platforms' },
            { value: '<2min', label: 'Per Video' },
          ].map((s) => (
            <div key={s.label}>
              <p className="text-3xl sm:text-4xl font-bold text-violet-400">{s.value}</p>
              <p className="text-sm text-zinc-500 mt-1">{s.label}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ── CTA ── */}
      <section className="py-20 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <h2 className="text-2xl sm:text-3xl font-semibold mb-4">
            Ready to create?
          </h2>
          <p className="text-zinc-500 mb-8">
            Start generating professional videos in minutes. No credit card required.
          </p>
          <button
            onClick={() => router.push('/auth/signin')}
            className="group bg-violet-600 text-white px-10 py-3.5 rounded-lg text-lg font-medium hover:bg-violet-500 transition-all inline-flex items-center gap-2"
          >
            Get Started Free
            <ArrowRight className="w-5 h-5 group-hover:translate-x-0.5 transition-transform" />
          </button>

        </div>
      </section>

      {/* ── Footer ── */}
      <footer className="py-6 px-4 border-t border-white/[0.06]">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-center gap-4 text-sm text-zinc-600">
          <span>&copy; {new Date().getFullYear()} IntelliSpace AI</span>
          <span className="hidden sm:inline">|</span>
          <a href="/privacy" className="hover:text-violet-400 transition-colors">
            Privacy Policy
          </a>
          <span className="hidden sm:inline">|</span>
          <a href="/terms" className="hover:text-violet-400 transition-colors">
            Terms of Service
          </a>
          <span className="hidden sm:inline">|</span>
          <a
            href={`mailto:${SUPPORT_EMAIL}`}
            className="hover:text-violet-400 transition-colors"
          >
            {SUPPORT_EMAIL}
          </a>
        </div>
      </footer>
    </div>
  );
}
